/**
 * Ключи i18n и производные заключений (FE-250).
 *
 * Чистые функции без React: их можно проверить отдельно от разметки.
 *
 * **Backend не присылает готовых строк.** Он отдаёт коды и числа; собрать текст
 * — работа этого модуля и i18n (CLAUDE.md §6). Код без перевода на экране
 * показывать нельзя: сырой `RECOMMENDED_TOP_RANKED` — утечка внутренностей.
 */

import type { Conclusion, ConclusionSet, FinalCheck } from './types';

/** Ключ i18n формулировки рекомендации. */
export function recommendationKey(code: string): string {
  return `conclusion.recommendation.${code}`;
}

/** Ключ i18n названия компонента ранжирования (§38.1). */
export function factorKey(code: string): string {
  return `conclusion.factor.${code}`;
}

/** Ключ i18n причины «не рекомендуем ни один» (§38.1). */
export function noRecommendationKey(code: string): string {
  return `conclusion.noRecommendation.${code}`;
}

/** Ключ i18n сверяемого поля финальной сверки (§38.3). */
export function discrepancyFieldKey(code: string): string {
  return `conclusion.discrepancy.field.${code}`;
}

/** Ключ i18n стороны расхождения: объявление, мандат, ответ, предложение. */
export function discrepancySourceKey(code: string): string {
  return `conclusion.discrepancy.source.${code}`;
}

/** Ключ i18n пункта чек-листа «что проверить лично» (§38.2). */
export function checklistKey(code: string): string {
  return `conclusion.checklist.${code}`;
}

/** Ключ i18n вопроса каталога — подпись строки «со слов автохауса». */
export function questionKey(code: string): string {
  return `negotiation.question.${code}`;
}

/** Рекомендованный вариант набора. `null` — не рекомендован ни один. */
export function recommended(set: ConclusionSet | null | undefined): Conclusion | null {
  return set?.items.find((item) => item.is_recommended) ?? null;
}

/**
 * Порядок показа: рекомендованный первым, затем по рангу, выбывшие в конце.
 *
 * Рекомендованный стоит первым и помечен, но **не крупнее остальных**: вёрстка
 * не должна подсказывать выбор, который делает пользователь (§38.4).
 */
export function ordered(items: Conclusion[]): Conclusion[] {
  return [...items].sort((left, right) => {
    if (left.is_recommended !== right.is_recommended) return left.is_recommended ? -1 : 1;
    if (left.rank === right.rank) return 0;
    if (left.rank === null) return 1;
    if (right.rank === null) return -1;
    return left.rank - right.rank;
  });
}

/**
 * Заблокировано ли согласие с предложением.
 *
 * Признак приходит с сервера (`blocks_acceptance`), а не вычисляется по
 * состоянию экрана: неактивная кнопка — подсказка, а правило живёт в сервисном
 * слое и отказывает и прямому запросу (§38.3).
 */
export function acceptanceBlocked(check: FinalCheck | null): boolean {
  return check?.blocks_acceptance ?? false;
}
